import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
} from "@roberttamas/ui";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { createOrder } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { clearBuyIntent, loadBuyIntent } from "@/lib/buy-intent";
import { toAppError } from "@/lib/errors";
import { formatAmount } from "@/lib/pricing";
import { usePublishedPricing } from "@/lib/pricing-public";
import type { OrderCustomer, PricingPreset, PricingTier } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Selection {
  tier: PricingTier;
  preset: PricingPreset | null;
  currency: string;
  amountMinor: number;
  locale: string;
}

/**
 * Order summary + billing form. The displayed price is advisory — the
 * backend re-derives it from the published pricing and freezes it into
 * the order, so what the buyer confirms here is re-checked server-side.
 */
export function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pricing = usePublishedPricing();

  const [intent] = useState(() => loadBuyIntent());
  const [presetOverride, setPresetOverride] = useState<string | null>(null);

  const [customerType, setCustomerType] = useState<"person" | "company">("person");
  const [name, setName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [ico, setIco] = useState("");
  const [dic, setDic] = useState("");
  const [email, setEmail] = useState(user?.email ?? "");

  const selection: Selection | null = useMemo(() => {
    const data = pricing.data;
    if (!data) return null;
    const tier =
      data.tiers.find((t) => t.id === intent?.tierId) ??
      data.tiers.find((t) => t.highlighted) ??
      data.tiers[0];
    if (!tier) return null;

    const locale = intent?.locale ?? data.config.defaultLocale;
    const currency =
      data.config.localeCurrency[locale] ??
      data.config.localeCurrency[data.config.defaultLocale] ??
      data.config.currencies[0] ??
      "CZK";

    let preset: PricingPreset | null = null;
    if (tier.presets && tier.presets.length > 0) {
      preset =
        tier.presets.find((p) => p.id === (presetOverride ?? intent?.presetId)) ??
        tier.presets.find((p) => p.isDefault) ??
        tier.presets[0] ??
        null;
    }

    const amountMinor = preset ? preset.prices[currency] : tier.prices?.[currency];
    if (amountMinor === undefined) return null;
    return { tier, preset, currency, amountMinor, locale };
  }, [pricing.data, intent, presetOverride]);

  const mutation = useMutation({
    mutationFn: (input: { selection: Selection; customer: OrderCustomer }) =>
      createOrder({
        tierId: input.selection.tier.id,
        presetId: input.selection.preset?.id ?? null,
        locale: input.selection.locale,
        customer: input.customer,
      }),
    onSuccess: (order) => {
      clearBuyIntent();
      navigate(`/my-orders/${order.orderId}`, { replace: true });
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  if (pricing.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (pricing.isError || !selection) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("buy.unavailable")}
        </CardContent>
      </Card>
    );
  }

  const copy = selection.tier.copy[selection.locale] ?? Object.values(selection.tier.copy)[0];

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mutation.isPending) return;
    if (customerType === "company" && ico.trim() === "") {
      toast.error(t("checkout.icoRequired"));
      return;
    }
    const customer: OrderCustomer = {
      type: customerType,
      name: name.trim(),
      email: email.trim(),
      address: {
        street: street.trim() || null,
        city: city.trim() || null,
        postalCode: postalCode.trim() || null,
        countryCode: "CZ",
      },
      ico: customerType === "company" ? ico.trim() || null : null,
      dic: customerType === "company" ? dic.trim() || null : null,
    };
    mutation.mutate({ selection, customer });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("checkout.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("checkout.subtitle")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("checkout.yourOrder")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-medium">{copy?.name ?? selection.tier.id}</p>
              {selection.preset && (
                <p className="text-sm text-muted-foreground">
                  {selection.preset.copy[selection.locale]?.detail ??
                    `${selection.preset.storageGb} GB, ${t("orderStatus.months", { count: selection.preset.months })}`}
                </p>
              )}
            </div>
            <p className="whitespace-nowrap text-xl font-semibold">
              {formatAmount(selection.amountMinor, selection.currency)}
            </p>
          </div>

          {selection.tier.presets && selection.tier.presets.length > 1 && (
            <div
              className="grid gap-1 rounded-md border p-1 sm:grid-cols-3"
              role="group"
              aria-label={t("buy.packageSizeAria")}
            >
              {selection.tier.presets.map((p) => {
                const active = p.id === selection.preset?.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPresetOverride(p.id)}
                    aria-pressed={active}
                    className={cn(
                      "rounded px-3 py-2 text-sm font-medium transition-colors",
                      active
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {p.copy[selection.locale]?.pill ?? `${p.storageGb} GB`}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("checkout.billingDetails")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className="grid grid-cols-2 gap-1 rounded-md border p-1"
              role="group"
              aria-label={t("checkout.customerTypeAria")}
            >
              {(
                [
                  ["person", t("checkout.person")],
                  ["company", t("checkout.company")],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCustomerType(value)}
                  aria-pressed={customerType === value}
                  className={cn(
                    "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                    customerType === value
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="name">
                  {customerType === "company" ? t("checkout.companyName") : t("checkout.fullName")}
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={200}
                />
              </div>

              {customerType === "company" && (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="ico">IČO</Label>
                    <Input
                      id="ico"
                      value={ico}
                      onChange={(e) => setIco(e.target.value)}
                      required
                      maxLength={20}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dic">{t("checkout.dicOptional")}</Label>
                    <Input
                      id="dic"
                      value={dic}
                      onChange={(e) => setDic(e.target.value)}
                      maxLength={20}
                    />
                  </div>
                </>
              )}

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="street">{t("checkout.street")}</Label>
                <Input
                  id="street"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  autoComplete="street-address"
                  maxLength={200}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="city">{t("checkout.city")}</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  autoComplete="address-level2"
                  maxLength={100}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="postalCode">{t("checkout.postalCode")}</Label>
                <Input
                  id="postalCode"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  autoComplete="postal-code"
                  maxLength={20}
                />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="billing-email">{t("checkout.invoiceEmail")}</Label>
                <Input
                  id="billing-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t("checkout.countryNote")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button
          type="submit"
          className="mt-6 w-full"
          size="lg"
          disabled={mutation.isPending}
        >
          {mutation.isPending
            ? t("checkout.creatingOrder")
            : t("checkout.continueToPayment", {
                amount: formatAmount(selection.amountMinor, selection.currency),
              })}
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {t("checkout.nothingCharged")}
        </p>
      </form>
    </div>
  );
}
